import type { UseQueryResult } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'

type QueryErrorState<TError> = Pick<
  UseQueryResult<unknown, TError>,
  'error' | 'errorUpdateCount' | 'isFetchedAfterMount'
>

/**
 * Stands in for the `onError` option TanStack Query 5 removed from `useQuery`. It fires once per
 * failed fetch made while this component is mounted, so a cached error does not fire again when
 * the component remounts.
 */
export const useOnQueryError = <TError>(
  { error, errorUpdateCount, isFetchedAfterMount }: QueryErrorState<TError>,
  onError: (error: TError) => void
): void => {
  const onErrorRef = useRef(onError)

  // Declared before the error effect so it always calls the handler from the committed render.
  useEffect(() => {
    onErrorRef.current = onError
  })

  // `errorUpdateCount` is a dependency so a failure that rejects with the same error object still fires.
  useEffect(() => {
    if (!error || !isFetchedAfterMount) return

    onErrorRef.current(error)
  }, [error, errorUpdateCount, isFetchedAfterMount])
}
